import {
  Component,
  Inject,
  NgZone,
  PLATFORM_ID,
  Input,
  OnDestroy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { TermInfo } from '../../../../../server/types';
import { DataService } from '@/shared/services';
import * as _ from 'lodash-es';
import { stringToColor } from '@/core/utils';

interface GPAChartData extends TermInfo {
  program?: string;
  columnSettings?: { fill: am5.Color };
}

@Component({
  selector: 'program-student-chart',
  templateUrl: './program-student-chart.component.html',
  styleUrls: ['./program-student-chart.component.css'],
  standalone: false,
})
export class ProgramStudentChartComponent implements OnDestroy {
  @Input() studentTermPrograms!: TermInfo[];
  @Input() studentTermGPAs!: TermInfo[];

  public root!: am5.Root;

  // chart variable
  private chart!: am5xy.XYChart;

  // data series variable
  private series1!: am5xy.ColumnSeries; //student university hours fall semester

  // Define variable for chart data
  private studentChartData: GPAChartData[] = [];

  // private prevDataLen: number = 0;

  constructor(
    private dataService: DataService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.root = am5.Root.new('programChart');
    this.reorganizeStudentChartData();
  }

  ngOnChanges() {
    this.reorganizeStudentChartData();

    // reset data again after first initiation
    if (this.series1) {
      this.resetSeriesData();
    }
  }

  ngAfterViewInit() {
    this.generateGraph();
  }

  // called once the component is destroyed
  ngOnDestroy() {
    // Clean up chart when the component is removed
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
    });
  }

  generateGraph() {
    this.root.setThemes([am5themes_Animated.new(this.root)]);

    // Remove amcharts logo below graphs
    this.root._logo!.dispose();

    // remove previous chart in root
    if (this.chart) {
      this.root.container.children.pop();
    }

    this.chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        layout: this.root.verticalLayout,
      }),
    );

    let xRenderer = am5xy.AxisRendererX.new(this.root, { minGridDistance: 30 });
    xRenderer.labels.template.setAll({
      rotation: -45,
      centerY: am5.p50,
      centerX: am5.p100,
      paddingRight: 15,
    });

    // Create axes
    let xAxis = this.chart.xAxes.push(
      am5xy.CategoryAxis.new(this.root, {
        categoryField: 'term',
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(this.root, {}),
      }),
    );

    xAxis.data.setAll(this.studentChartData);

    let yAxis = this.chart.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        min: 0,
        max: 4,
        renderer: am5xy.AxisRendererY.new(this.root, {}),
      }),
    );

    if (this.series1) {
      this.chart.series.pop();
    }

    // Add series
    this.series1 = this.chart.series.push(
      am5xy.ColumnSeries.new(this.root, {
        // name: 'GPA',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'value',
        categoryXField: 'term',
      }),
    );

    // Add data labels
    // const rootCopy = this.root;
    // this.series1.bullets.push(function () {
    //   return am5.Bullet.new(rootCopy, {
    //     locationY: 0.5,
    //     sprite: am5.Label.new(rootCopy, {
    //       text: '{valueY}\n{program}',
    //       fill: am5.color(0xffffff),
    //       centerY: am5.p50,
    //       centerX: am5.p50,
    //       populateText: true,
    //       shadowColor: am5.color(0x000000),
    //       shadowBlur: 5,
    //       shadowOffsetX: 2,
    //       shadowOffsetY: 2,
    //     }),
    //   });
    // });

    this.series1.data.setAll(this.studentChartData);

    //Set tooltip data
    this.series1.columns.template.setAll({
      tooltipText: 'GPA: {valueY} | {program}',
      tooltipY: am5.percent(10),
      templateField: 'columnSettings',
    });

    // Set cursor
    // this.chart.set('cursor', am5xy.XYCursor.new(this.root, {}));

    // Add legend
    const legend = this.chart.children.push(
      am5.Legend.new(this.root, {
        nameField: 'name',
        fillField: 'color',
        strokeField: 'color',
        // centerX: am5.percent(50),
        // x: am5.percent(50),
      }),
    );
    const legendData = _.uniqBy(
      this.series1.dataItems.map((dataItem) => {
        return {
          program: (dataItem.dataContext as GPAChartData).program,
          color: (dataItem.dataContext as GPAChartData).columnSettings?.fill,
        };
      }),
      'program',
    );

    legend.data.setAll(
      legendData.map((item) => ({
        name: item.program,
        color: item.color,
      })),
    );

    // Make stuff animate on load
    this.chart.appear(1000, 100);
    this.series1.appear();

    // Add label in Y-axis
    yAxis.axisHeader.children.push(
      am5.Label.new(this.root, {
        text: 'GPA',
        fontFamily: "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
        fontSize: 20,
        fontWeight: '500',
      }),
    );

    // Add label in X-axis
    this.chart.children.unshift(
      am5.Label.new(this.root, {
        text: 'Semesters',
        fontSize: 20,
        fontFamily: "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
        fontWeight: '500',
        textAlign: 'center',
        x: am5.percent(50),
        centerX: am5.percent(50),
        y: am5.percent(100),
        centerY: am5.percent(100),
        paddingTop: 0,
        paddingBottom: 0,
      }),
    );
  }

  // function to flatten data and add program to each objects in data
  reorganizeStudentChartData() {
    this.studentChartData = this.studentTermGPAs.map(({ term, value }) => ({
      term,
      value,
    }));
    this.studentChartData.forEach((record, index) => {
      record.program = this.studentTermPrograms[index].value as string;
    });

    this.studentChartData.length > 0 && this.checkProgramChange();
  }

  // // set color to each program
  // stringToColour(str: string) {
  //   let hash = 0;
  //   for (let i = 0; i < str.length; i++) {
  //     hash = str.charCodeAt(i) + ((hash << 5) - hash);
  //   }
  //   let colour = '#';
  //   for (let i = 0; i < 3; i++) {
  //     const value = (hash >> (i * 8)) & 0xff;
  //     colour += ('00' + value.toString(16)).substring(-2);
  //   }
  //   return colour;
  // }

  // check for change in program and add additional bar graph styling after the change
  checkProgramChange() {
    const columnStyle: { [key: string]: any } = {};

    this.studentChartData.forEach((sem, index) => {
      let program = sem.program!;
      if (!columnStyle[program] && program) {
        columnStyle[program] = {
          fill: am5.color(stringToColor(program, index)),
        };
      }
      sem.columnSettings = columnStyle[program];
    });
  }

  // reset data in series for incoming new data
  resetSeriesData() {
    this.generateGraph();
  }

  // Run the function only in the browser
  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        f();
      });
    }
  }

  exportChart() {
    this.dataService.exportAmChartsAsPNG(this.root, 'gpa-with-program-chart');
  }
}
